import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { ActionButtonSlice } from "../components/atoms/content-authoring-tabular/actionButtonSlice";
import { xapiSlice } from "../components/molecules/analytics/xapiSlice";
import BreadcrumbSlice from "../components/molecules/breadcrumb/slices/breadcrumbSlice";
import { ContentFaqGetApi } from "../components/molecules/faq/slices/contentFaq";
import { NotificationApi } from "../components/molecules/notification/slice/notificationSlice";
import SurveySlice from "../components/molecules/surveyjs/survey-preview/surveySliceActions";
import { SurveyApi } from "../components/molecules/surveyjs/survey-slice/surveySlice";
import { AdminDashboardApiSlice } from "../pages/admin-dashboard/slices/api/adminDashboardApiSlice";
import { AttendanceApi } from "../pages/agenda-attendance/slices/attendanceSlice";
import { AgendaApi } from "../pages/agenda-creation/features/api/apiSlice";
import { UtilityUser } from "../pages/agenda-creation/features/api/getUsers";
import {
  eventMetaSlice,
  LpMetaSlice,
  SelectedMeta
} from "../pages/authoring-dashboard/learning-path-library/learning-path-tans/slice/metaSlice";
import { AuthoringViewSlice } from "../pages/authoring-screens/authoringViewSlice";
import { CalendarDashboardAdminApi } from "../pages/calendar-dashboard/calendar-dashboard-admin/slices/calendarDashboardAdminApiSlice";
import CalendarDashboardAdminSlice from "../pages/calendar-dashboard/calendar-dashboard-admin/slices/calendarDashboardAdminSlice";
import { CalendarDashboardGlobalApi } from "../pages/calendar-dashboard/calendar-dashboard-global/slices/calendarDashboardGlobalApiSlice";
import CalendarDashboardGlobalMapSlice from "../pages/calendar-dashboard/calendar-dashboard-global/slices/calendarDashboardGlobalMapSlice";
import CalendarDashboardGlobalSlice from "../pages/calendar-dashboard/calendar-dashboard-global/slices/calendarDashboardGlobalSlice";
import { CalendarDashboardLearnerApi } from "../pages/calendar-dashboard/calendar-dashboard-learner/slices/calendarDashboardLearnerApiSlice";
import CalendarDashboardLearnerSlice from "../pages/calendar-dashboard/calendar-dashboard-learner/slices/calendarDashboardLearnerSlice";
import { CalendarDashboardLearnerSpeakerApi } from "../pages/calendar-dashboard/calendar-dashboard-learner/slices/calendarDashboardLearnerSpeakerApiSlice";
import { ContentAuthoringApi } from "../pages/content-authoring/contentApiSlice/contentApiSlice";
import NotesAPI from "../pages/content-viewer/notes-widget/notes-slice/notesSlice";
import { ContentLearnerApi } from "../pages/content-viewer/slices/contentLearnerApiSlice";
import { ContentProgressApi } from "../pages/content-viewer/slices/contentProgressApiSlice";
import { DiscussionApi } from "../pages/content-viewer/slices/discussionApiSlice";
import { ContentApi } from "../pages/content-viewer/slices/contentSlice";
import ContentStateSlice from "../pages/content-viewer/slices/contentStateSlice";
import { ContentLaunchUrlApi } from "../pages/content-viewer/slices/getLaunchUrl";
import LearningAssistanceSlice from "../pages/course-player/learner-assistance/slice/learningAssistanceSlice";
import { LearningAssistanceApi } from "../pages/course-player/learner-assistance/slice/responseApiSlice";
import { CoursePlayerApi } from "../pages/course-player/slice/courseSlice";
import CourseStateSlice from "../pages/course-player/slice/courseStateSlice";
import { FetchCourseApi } from "../pages/course-player/slice/fetchCourseSlice";
import { CreateEventApi } from "../pages/create-event/slices/createEventApiSlice";
import { EventAiModalApi } from "../pages/create-event/slices/eventAiModalSlice";
import { SessionApi } from "../pages/create-session/slices/sessionSlice";
import { UtilityApi } from "../pages/create-session/slices/utilitySlice";
import { StatusUpdateEventApi } from "../pages/event-creation-workflow/slice/statusUpdateEventApiSlice";
import { AllSessionApi } from "../pages/event-creation-workflow/slices/allSessionApiSlice";
import { EventSurveyApi } from "../pages/event-creation-workflow/slices/eventSurveysApiSlice";
import {
  eventDetailsSlice,
  sessionDetailsSlice,
  agendaDetailsSlice
} from "../pages/event-creation-workflow/slices";
import { EventRegistrationDetailsSlice } from "../pages/event-session-details/slices/eventRegistrationDetailsSlice";
import { ParticipantSearchAPI } from "../pages/event-session-details/slices/instructorActions";
import { LearnerAgendaApi } from "../pages/event-session-details/slices/learnerAgendaSlice";
import { LearnerProgressApi } from "../pages/event-session-details/slices/learnerProgressSlice";
import { ReferenceMaterialApiSlice } from "../pages/event-session-details/slices/refferenceMaterialSlice";
import { AgendaSearchApi } from "../pages/event-session-details/slices/searchAgendaSlice";
import StakeholderSlice from "../pages/event-session-details/slices/stakeholderSlice";
import { UserSearchAPi } from "../pages/event-session-details/slices/userSearchSlice";
import { GcpUploadApi } from "../pages/eventCreationWorkflowV2/slices/gcp-upload-api-";
import { UploadApi } from "../pages/eventCreationWorkflowV2/slices/uploadApi";
import { UploadGetApi } from "../pages/eventCreationWorkflowV2/slices/uploadGetApi";
import { UserGroupAuthoringApi } from "../pages/group-authoring/api/userGroupAuthoringApis";
import { UserGroupSlice } from "../pages/group-authoring/slices/userGroupSlice";
import { AllUserLearnings } from "../pages/learning-admin-dashboard/slices/allUserLearnings";
import { AssignByContent } from "../pages/learning-admin-dashboard/slices/assignByContentSlice";
import { CertificateApiSlice } from "../pages/learning-path/slices/certificateSlice";
import LearningPathProgressSlice from "../pages/learning-path/slices/learningPathProgressSlice";
import CourseProgressSlice from "../pages/learning-path/slices/courseProgressSlice";
import LearningPathSlice from "../pages/learning-path/slices/learningPathSlice";
import { ManageEventParticipantsApiSlice } from "../pages/manage-event-participants/slices/api/manageEventParticipantsApiSlice";
import { AssignedContentByUser } from "../pages/manager-dashboard/slice/assignedContentByUser";
import { TeamMembersSkillsApi } from "../pages/manager-dashboard/slice/skillsSlice";
import { TeamMembersApi } from "../pages/manager-dashboard/slice/teammemberSlice";
import { AssessmentResultApi } from "../pages/my-activity-dashboard/pages/slice/assessmentResultApi";
import { AssignLearningApi } from "../pages/my-activity-dashboard/pages/slice/assignedLearningApi";
import { CompletedLearningApi } from "../pages/my-activity-dashboard/pages/slice/completedLearningSlice";
import { ProgressApi } from "../pages/my-activity-dashboard/pages/slice/progressApi";
import { AiSummary } from "../pages/podcast-episode/slice/AiSummary";
import { EventCommentApi } from "../pages/preview-event/slices/eventCommentSlice";
import { LearningProgressApi } from "../pages/profile-page/goals-objectives/slices/learningProgressSlice";
import { ManagerDashboardApi } from "../pages/profile-page/reportees/slices/managerDashSlice";
import { CommonControllerApi } from "../pages/question-authoring/api/commonControllerApi";
import { fibAssessmentSliceApi } from "../pages/question-authoring/api/fibAssessmentApi";
import { McqAssessmentSliceApi } from "../pages/question-authoring/api/mcqAssessmentApi";
import { QuestionsApi } from "../pages/question-authoring/api/questionsApi";
import QuestionStateSlice from "../pages/question-authoring/question-store/question-store-slice";
import EventStateSlice from "../pages/event-session-details/event-store/event-store-slice";
import NewQuestionStateSlice from "../pages/question-authoring/slice/newQuestionStateSlice";
import { ContentDomain } from "../pages/search-page/components/contetDomainSlice";
import GlobalSearchAPI from "../pages/search-page/components/globalSearchSlice";
import { SearchAPI } from "../pages/search-suggestion/searchAPI";
import { SearchBarGqlApi } from "../pages/search-suggestion/searchBarAPI";
import { SkillsUtility } from "../utils/getSkills";
import { SpeakersUtility } from "../utils/getSpeakers";
import { UserUtility } from "../utils/getUser";
import { SearchLocationApiSlice } from "../utils/searchLocation";
import { SearchSkillsApiSlice } from "../utils/searchSkills";
import { apiSlice } from "./slices/api/apiSlice";
import { LearningPathAuthoringApiSlice } from "./slices/api/authoring/learningPathAuthoringApiSlice";
import { GetUserBySearchKey } from "./slices/api/getUserBySearchkey";
import { LearningPathApiSlice } from "./slices/api/learning-path/learningPathApiSlice";
import { SocialInteractionsApiSlice } from "./slices/api/social-interactions/socialInteractionsApiSlice";
import { UserInfoApiSlice } from "@lxp-app/store/slices/api/userInfoApiSlice";
import { VideoPlayerApiSlice } from "./slices/api/videoPlayer/videoPlayerApiSlice";
import { appLoaderSlice } from "./slices/appLoaderSlice";
import { AppMetaDataSlice } from "./slices/appMetaDataSlice";
import LearningPathAddNewContentSlice from "./slices/authoring/learningPathAddNewContentSlice";
import LearningPathAuthoringSlice from "./slices/authoring/learningPathAuthoringSlice";
import CourseAuthoringSlice from "./slices/course-authoring-store/courseAuthoringSlice";
import DarkmodeSlice from "./slices/darkmodeSlice";
import EventMetaSlice from "./slices/event-authoring/eventMetaSlice";
import { QueryArgsSlice } from "./slices/query-args/initialState";
import { queryArgsSlice } from "./slices/query-args/queryArgsSlice";
import searchQuerySlice from "./slices/searchQuerySlice";
import UserInfoSlice from "@lxp-app/store/slices/userInfoSlice";
import AssessmentSlice from "@assessment/store/slices/assessmentSlice";
import SharedContent, {
  SharedContentSlice
} from "../components/molecules/notification/slice/getSharedContentNotification";
import AssessmentAuthSlice from "../pages/assessment-library/slices/assessmentAuthSlice";
import SectionListSlice from "../pages/assessment-library/slices/sectionListSlice";
import EventReducer, {
  EVENT_SLICE
} from "../pages/event-review/slices/eventSessionSlice";
import { ManageRoleDashboardAPI } from "../pages/manage-role/manageRoleDashboard/manageRoleDashboardAPI";
import { AssessmentApiSlice } from "@assessment/store/slices/api/assessmentApiSlice";
import { UpdateEventMetaSlice } from "../pages/update-event-info/eventMetaDataSlice";
import UpdateEventPostPublishApi from "../pages/update-event-info/api/updateAgenda";
import ActivityDashboardSlice from "@lxp-app/pages/my-activity-dashboard/pages/slice/activityDashboardSlice";
import { AssignRoleAPISlice } from "@lxp-app/pages/manage-role/assignAndEditRoles/assignRoleAPI";
import AssignEditRoleSLice from "@lxp-app/pages/manage-role/assignAndEditRoles/assignEditRoleSlice";
import BulkUploadSlice from "@lxp-app/pages/question-authoring/slice/bulkUploadSlice";
import { BulkUploadApi } from "@lxp-app/pages/question-authoring/api/bulkUploadApi";
import { DetailedAssessmentResultsApi } from "@lxp-app/pages/question-authoring/api/detailedAssessmentResultsApi";
import { ProctoringDataApi } from "@lxp-app/pages/question-authoring/api/proctoringDataApi";
import { RunCodeApi } from "@lxp-app/pages/question-authoring/api/runCodeApi";
import { SubjectiveAPI } from "@lxp-app/pages/question-authoring/api/subjectiveAPI";
import { CommonControllerApiV2 } from "@lxp-app/pages/question-authoring/api/commonControllerApiV2";
import navigateDetailsSlice from "../pages/event-creation-workflow/slices/navigatedetailsslice";
import EnabledFeedbackBtnSlice from "@lxp-app/pages/learning-path/slices/enabledFeedbackBtnSlice";
import { aiInterviewApiSlice } from "@ai-interview/store/api/aiInterviewApi";
import chatReducer from "@ai-interview/store/chatSlice";
import sessionReducer from "@ai-interview/store/sessionSlice";
import CommonConfigApi from "@lxp-app/store/slices/api/commonConfigApi";
import QuestionPreviewSlice from "@lxp-app/pages/question-authoring/slice/questionPreviewSlice";
import RemarksApi from "@lxp-app/store/slices/api/remarksApi";
import AssessmentReportSlice from "@lxp-app/pages/question-authoring/candidateResultSummaryForAuthoring/slices/assessmentReportSlice";
import EndorsementSlice from "./slices/endorsementSlice";
import EventExtensionSlice from "../pages/event-extension/slice/eventExtensionSlice";
import { ExtentEventApi } from "@lxp-app/pages/event-extension/api/extendEventApi";
import GroupAssignmentDataSlice from "@lxp-app/pages/content-assignment/slice/groupAssignmentDataSlice";

const reducer = combineReducers({
  [appLoaderSlice.name]: appLoaderSlice.reducer,
  [CommonConfigApi.reducerPath]: CommonConfigApi.reducer,
  [RemarksApi.reducerPath]: RemarksApi.reducer,
  [AppMetaDataSlice.name]: AppMetaDataSlice.reducer,
  [AllSessionApi.reducerPath]: AllSessionApi.reducer,
  [CreateEventApi.reducerPath]: CreateEventApi.reducer,
  [SessionApi.reducerPath]: SessionApi.reducer,
  [AttendanceApi.reducerPath]: AttendanceApi.reducer,
  calendarDashboardGlobal: CalendarDashboardGlobalSlice.reducer,
  calendarDashboardLearner: CalendarDashboardLearnerSlice.reducer,
  calendarDashboardAdmin: CalendarDashboardAdminSlice.reducer,
  [CalendarDashboardGlobalApi.reducerPath]: CalendarDashboardGlobalApi.reducer,
  [CalendarDashboardLearnerApi.reducerPath]:
    CalendarDashboardLearnerApi.reducer,
  [CalendarDashboardLearnerSpeakerApi.reducerPath]:
    CalendarDashboardLearnerSpeakerApi.reducer,
  [CalendarDashboardAdminApi.reducerPath]: CalendarDashboardAdminApi.reducer,
  [AgendaApi.reducerPath]: AgendaApi.reducer,
  [SkillsUtility.reducerPath]: SkillsUtility.reducer,
  [UtilityApi.reducerPath]: UtilityApi.reducer,
  [SpeakersUtility.reducerPath]: SpeakersUtility.reducer,
  [SurveyApi.reducerPath]: SurveyApi.reducer,
  [UtilityUser.reducerPath]: UtilityUser.reducer,
  [StatusUpdateEventApi.reducerPath]: StatusUpdateEventApi.reducer,
  [SearchSkillsApiSlice.reducerPath]: SearchSkillsApiSlice.reducer,
  [UserInfoSlice.name]: UserInfoSlice.reducer,
  [UserInfoApiSlice.reducerPath]: UserInfoApiSlice.reducer,
  [EventCommentApi.reducerPath]: EventCommentApi.reducer,
  [UserUtility.reducerPath]: UserUtility.reducer,
  [SearchLocationApiSlice.reducerPath]: SearchLocationApiSlice.reducer,
  [AgendaSearchApi.reducerPath]: AgendaSearchApi.reducer,
  [LearnerAgendaApi.reducerPath]: LearnerAgendaApi.reducer,
  calendarDashboardGlobalMap: CalendarDashboardGlobalMapSlice.reducer,
  searchQuery: searchQuerySlice,
  [NotificationApi.reducerPath]: NotificationApi.reducer,
  [UploadApi.reducerPath]: UploadApi.reducer,
  [UploadGetApi.reducerPath]: UploadGetApi.reducer,
  [EventRegistrationDetailsSlice.reducerPath]:
    EventRegistrationDetailsSlice.reducer,
  [SearchBarGqlApi.reducerPath]: SearchBarGqlApi.reducer,
  [SearchAPI.reducerPath]: SearchAPI.reducer,
  [queryArgsSlice.name]: queryArgsSlice.reducer,
  [GlobalSearchAPI.reducerPath]: GlobalSearchAPI.reducer,
  [ContentApi.reducerPath]: ContentApi.reducer,
  [ContentLaunchUrlApi.reducerPath]: ContentLaunchUrlApi.reducer,
  [ContentFaqGetApi.reducerPath]: ContentFaqGetApi.reducer,
  [LearningPathApiSlice.reducerPath]: LearningPathApiSlice.reducer,
  [LearningPathSlice.reducerPath]: LearningPathSlice.reducer,
  [LearningPathProgressSlice.reducerPath]: LearningPathProgressSlice.reducer,
  [CourseProgressSlice.reducerPath]: CourseProgressSlice.reducer,
  [EnabledFeedbackBtnSlice.reducerPath]: EnabledFeedbackBtnSlice.reducer,
  [NotesAPI.reducerPath]: NotesAPI.reducer,
  [GetUserBySearchKey.reducerPath]: GetUserBySearchKey.reducer,
  [ContentStateSlice.reducerPath]: ContentStateSlice.reducer,
  [ContentProgressApi.reducerPath]: ContentProgressApi.reducer,
  [ContentLearnerApi.reducerPath]: ContentLearnerApi.reducer,
  [DiscussionApi.reducerPath]: DiscussionApi.reducer,
  [AdminDashboardApiSlice.reducerPath]: AdminDashboardApiSlice.reducer,
  [CompletedLearningApi.reducerPath]: CompletedLearningApi.reducer,
  [AssignLearningApi.reducerPath]: AssignLearningApi.reducer,
  [ParticipantSearchAPI.reducerPath]: ParticipantSearchAPI.reducer,
  [UserSearchAPi.reducerPath]: UserSearchAPi.reducer,
  [AssessmentResultApi.reducerPath]: AssessmentResultApi.reducer,
  breadCrumb: BreadcrumbSlice.reducer,
  [LearnerProgressApi.reducerPath]: LearnerProgressApi.reducer,
  [DarkmodeSlice.name]: DarkmodeSlice.reducer,
  [LearningProgressApi.reducerPath]: LearningProgressApi.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [ManagerDashboardApi.reducerPath]: ManagerDashboardApi.reducer,
  [TeamMembersApi.reducerPath]: TeamMembersApi.reducer,
  [TeamMembersSkillsApi.reducerPath]: TeamMembersSkillsApi.reducer,
  [ContentAuthoringApi.reducerPath]: ContentAuthoringApi.reducer,
  [LearningPathAuthoringSlice.reducerPath]: LearningPathAuthoringSlice.reducer,
  [StakeholderSlice.reducerPath]: StakeholderSlice.reducer,
  [LearningPathAuthoringApiSlice.reducerPath]:
    LearningPathAuthoringApiSlice.reducer,
  [CourseAuthoringSlice.reducerPath]: CourseAuthoringSlice.reducer,
  [SectionListSlice.reducerPath]: SectionListSlice.reducer,
  [SurveySlice.reducerPath]: SurveySlice.reducer,
  [QuestionStateSlice.reducerPath]: QuestionStateSlice.reducer,
  [EventStateSlice.reducerPath]: EventStateSlice.reducer,
  [NewQuestionStateSlice.reducerPath]: NewQuestionStateSlice.reducer,
  [LearningPathAddNewContentSlice.reducerPath]:
    LearningPathAddNewContentSlice.reducer,
  [xapiSlice.name]: xapiSlice.reducer,
  [ProgressApi.reducerPath]: ProgressApi.reducer,
  [AssignedContentByUser.reducerPath]: AssignedContentByUser.reducer,
  [AllUserLearnings.reducerPath]: AllUserLearnings.reducer,
  [AssignByContent.reducerPath]: AssignByContent.reducer,
  [EventSurveyApi.reducerPath]: EventSurveyApi.reducer,
  [CoursePlayerApi.reducerPath]: CoursePlayerApi.reducer,
  [FetchCourseApi.reducerPath]: FetchCourseApi.reducer,
  [CourseStateSlice.reducerPath]: CourseStateSlice.reducer,
  [VideoPlayerApiSlice.reducerPath]: VideoPlayerApiSlice.reducer,
  [EventAiModalApi.reducerPath]: EventAiModalApi.reducer,
  [EventMetaSlice.reducerPath]: EventMetaSlice.reducer,
  [GcpUploadApi.reducerPath]: GcpUploadApi.reducer,
  [ManageEventParticipantsApiSlice.reducerPath]:
    ManageEventParticipantsApiSlice.reducer,
  [LearningAssistanceSlice.reducerPath]: LearningAssistanceSlice.reducer,
  [LearningAssistanceApi.reducerPath]: LearningAssistanceApi.reducer,
  [ReferenceMaterialApiSlice.reducerPath]: ReferenceMaterialApiSlice.reducer,
  [LpMetaSlice.reducerPath]: LpMetaSlice.reducer,
  [eventMetaSlice.reducerPath]: eventMetaSlice.reducer,
  [SelectedMeta.reducerPath]: SelectedMeta.reducer,
  [SocialInteractionsApiSlice.reducerPath]: SocialInteractionsApiSlice.reducer,
  [ContentDomain.reducerPath]: ContentDomain.reducer,
  [AiSummary.reducerPath]: AiSummary.reducer,
  [CertificateApiSlice.reducerPath]: CertificateApiSlice.reducer,
  [QuestionsApi.reducerPath]: QuestionsApi.reducer,
  [McqAssessmentSliceApi.reducerPath]: McqAssessmentSliceApi.reducer,
  [fibAssessmentSliceApi.reducerPath]: fibAssessmentSliceApi.reducer,
  [CommonControllerApi.reducerPath]: CommonControllerApi.reducer,
  [ActionButtonSlice.reducerPath]: ActionButtonSlice.reducer,
  [AuthoringViewSlice.reducerPath]: AuthoringViewSlice.reducer,
  [UserGroupSlice.reducerPath]: UserGroupSlice.reducer,
  [UserGroupAuthoringApi.reducerPath]: UserGroupAuthoringApi.reducer,
  [UpdateEventMetaSlice.reducerPath]: UpdateEventMetaSlice.reducer,
  [UpdateEventPostPublishApi.reducerPath]: UpdateEventPostPublishApi.reducer,
  [AssessmentSlice.reducerPath]: AssessmentSlice.reducer,
  [AssessmentApiSlice.reducerPath]: AssessmentApiSlice.reducer,
  [ProctoringDataApi.reducerPath]: ProctoringDataApi.reducer,
  [SharedContentSlice]: SharedContent,
  [AssessmentAuthSlice.reducerPath]: AssessmentAuthSlice.reducer,
  [ActivityDashboardSlice.reducerPath]: ActivityDashboardSlice.reducer,
  [EVENT_SLICE]: EventReducer,

  [ManageRoleDashboardAPI.reducerPath]: ManageRoleDashboardAPI.reducer,
  [AssignRoleAPISlice.reducerPath]: AssignRoleAPISlice.reducer,
  [AssignEditRoleSLice.reducerPath]: AssignEditRoleSLice.reducer,
  [BulkUploadSlice.reducerPath]: BulkUploadSlice.reducer,
  [BulkUploadApi.reducerPath]: BulkUploadApi.reducer,
  [DetailedAssessmentResultsApi.reducerPath]:
    DetailedAssessmentResultsApi.reducer,
  // [EVENT_SLICE]: EventReducer,
  [RunCodeApi.reducerPath]: RunCodeApi.reducer,
  [SubjectiveAPI.reducerPath]: SubjectiveAPI.reducer,
  [CommonControllerApiV2.reducerPath]: CommonControllerApiV2.reducer,
  eventDetails: eventDetailsSlice,
  sessionDetails: sessionDetailsSlice,
  agendaDetails: agendaDetailsSlice,
  navigateDetails: navigateDetailsSlice,
  [aiInterviewApiSlice.reducerPath]: aiInterviewApiSlice.reducer,
  chat: chatReducer,
  session: sessionReducer,
  [QuestionPreviewSlice.reducerPath]: QuestionPreviewSlice.reducer,
  assessmentReportSlice: AssessmentReportSlice.reducer,
  [EndorsementSlice.name]: EndorsementSlice.reducer,
  [GroupAssignmentDataSlice.name]: GroupAssignmentDataSlice.reducer,
  [EventExtensionSlice.name]: EventExtensionSlice.reducer,
  [ExtentEventApi.reducerPath]: ExtentEventApi.reducer
});

export const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().prepend(
      CommonConfigApi.middleware,
      RemarksApi.middleware,
      CreateEventApi.middleware,
      SessionApi.middleware,
      AttendanceApi.middleware,
      UtilityApi.middleware,
      SkillsUtility.middleware,
      CalendarDashboardGlobalApi.middleware,
      CalendarDashboardLearnerApi.middleware,
      CalendarDashboardLearnerSpeakerApi.middleware,
      CalendarDashboardAdminApi.middleware,
      AllSessionApi.middleware,
      AgendaApi.middleware,
      SpeakersUtility.middleware,
      UtilityUser.middleware,
      StatusUpdateEventApi.middleware,
      SearchSkillsApiSlice.middleware,
      UserInfoApiSlice.middleware,
      EventCommentApi.middleware,
      UserUtility.middleware,
      SearchLocationApiSlice.middleware,
      AgendaSearchApi.middleware,
      LearnerAgendaApi.middleware,
      NotificationApi.middleware,
      UploadApi.middleware,
      UploadGetApi.middleware,
      EventRegistrationDetailsSlice.middleware,
      SurveyApi.middleware,
      SearchBarGqlApi.middleware,
      SearchAPI.middleware,
      GlobalSearchAPI.middleware,
      ContentApi.middleware,
      ContentLaunchUrlApi.middleware,
      ContentFaqGetApi.middleware,
      LearningPathApiSlice.middleware,
      NotesAPI.middleware,
      GetUserBySearchKey.middleware,
      ContentProgressApi.middleware,
      ContentLearnerApi.middleware,
      DiscussionApi.middleware,
      AdminDashboardApiSlice.middleware,
      CompletedLearningApi.middleware,
      AssignLearningApi.middleware,
      ParticipantSearchAPI.middleware,
      UserSearchAPi.middleware,
      AssessmentResultApi.middleware,
      LearnerProgressApi.middleware,
      LearningProgressApi.middleware,
      ContentAuthoringApi.middleware,
      apiSlice.middleware,
      ManagerDashboardApi.middleware,
      TeamMembersApi.middleware,
      TeamMembersSkillsApi.middleware,
      LearningPathAuthoringApiSlice.middleware,
      ProgressApi.middleware,
      AssignedContentByUser.middleware,
      AllUserLearnings.middleware,
      AssignByContent.middleware,
      EventSurveyApi.middleware,
      CoursePlayerApi.middleware,
      FetchCourseApi.middleware,
      VideoPlayerApiSlice.middleware,
      EventAiModalApi.middleware,
      GcpUploadApi.middleware,
      ManageEventParticipantsApiSlice.middleware,
      LearningAssistanceApi.middleware,
      ReferenceMaterialApiSlice.middleware,
      SocialInteractionsApiSlice.middleware,
      CertificateApiSlice.middleware,
      QuestionsApi.middleware,
      McqAssessmentSliceApi.middleware,
      fibAssessmentSliceApi.middleware,
      CommonControllerApi.middleware,
      UserGroupAuthoringApi.middleware,
      UpdateEventPostPublishApi.middleware,
      AssessmentApiSlice.middleware,
      ManageRoleDashboardAPI.middleware,
      AssignRoleAPISlice.middleware,
      BulkUploadApi.middleware,
      DetailedAssessmentResultsApi.middleware,
      ProctoringDataApi.middleware,
      RunCodeApi.middleware,
      SubjectiveAPI.middleware,
      CommonControllerApiV2.middleware,
      aiInterviewApiSlice.middleware,
      ExtentEventApi.middleware
    ),
  devTools: true
});

export type RootState = ReturnType<typeof store.getState> & {
  queryArgsSlice: QueryArgsSlice;
};
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = useDispatch<AppDispatch>;
